<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { computed, ref, watch } from 'vue'

interface Country {
  label: string
  value: string | number
}

interface TransferMethod {
  label: string
  value: string | number
  title?: string
}

interface InternationalBankAccount {
  id: string | number
  bankName: string
  accountNumber?: string
  iban?: string
  ownerFullName?: string
  bankIcon?: string
}

interface Props {
  modelValue: string | number | null
  label?: string
  placeholder?: string
  disabled?: boolean
  error?: string

  countries: Country[]

  transferMethods: TransferMethod[]

  internationalAccounts?: InternationalBankAccount[]
  paypalAccounts?: InternationalBankAccount[]
  revolutAccounts?: InternationalBankAccount[]
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  label: 'حساب مقصد',
  placeholder: 'انتخاب کنید',
  error: '',
  countries: () => [],
  transferMethods: () => [],
  internationalAccounts: () => [],
  paypalAccounts: () => [],
  revolutAccounts: () => [],
})

const emit = defineEmits<(e: 'update:modelValue', value: string | number) => void>()

const isOpen = ref(false)
const isCreateAccountModalOpen = ref(false)
const wrapper = ref<HTMLElement | null>(null)
const loading = ref(false)

const selectedAccount = ref<string | number | null>(props.modelValue)

const selectedTransferMethods = ref<string[]>([])

onClickOutside(wrapper, () => {
  isOpen.value = false
})

const getTransferMethodType = (method: TransferMethod) => {
  const text = `
        ${method.label}
        ${method.value}
        ${method.title ?? ''}
    `.toLowerCase()

  if (text.includes('paypal') || text.includes('pay pal') || text.includes('پی پال')) {
    return 'PAYPAL'
  }

  if (text.includes('revolut') || text.includes('رولوت')) {
    return 'REVOLUT'
  }

  return 'INTERNATIONAL'
}

const groupedAccounts = computed(() => {
  const groups: Array<{
    transferMethod: string
    transferMethodLabel: string
    accounts: InternationalBankAccount[]
  }> = []

  let revolutAdded = false

  props.transferMethods.forEach((method) => {
    const type = getTransferMethodType(method)

    if (type === 'REVOLUT') {
      if (!revolutAdded) {
        groups.push({
          transferMethod: 'REVOLUT',
          transferMethodLabel: 'رولوت',
          accounts: props.revolutAccounts,
        })

        revolutAdded = true
      }

      return
    }

    if (type === 'PAYPAL') {
      groups.push({
        transferMethod: String(method.value),
        transferMethodLabel: method.label,
        accounts: props.paypalAccounts,
      })

      return
    }

    groups.push({
      transferMethod: String(method.value),
      transferMethodLabel: method.label,
      accounts: props.internationalAccounts,
    })
  })

  return groups
})

const selectedAccountData = computed(() => {
  const allAccounts = [...props.internationalAccounts, ...props.paypalAccounts, ...props.revolutAccounts]

  return allAccounts.find((account) => account.id === selectedAccount.value)
})

const toggleOpen = () => {
  if (props.disabled) {
    return
  }

  isOpen.value = !isOpen.value
}

const toggleTransferMethod = (transferMethod: string) => {
  const index = selectedTransferMethods.value.indexOf(transferMethod)

  if (index === -1) {
    selectedTransferMethods.value.push(transferMethod)
  } else {
    selectedTransferMethods.value.splice(index, 1)
  }
}

const isTransferMethodSelected = (transferMethod: string) => {
  return selectedTransferMethods.value.includes(transferMethod)
}

const updateValue = (value: string | number) => {
  selectedAccount.value = value

  emit('update:modelValue', value)

  // isOpen.value = false
}

const openCreateAccountModal = () => {
  isCreateAccountModalOpen.value = true

  isOpen.value = false
}

watch(
  () => props.modelValue,
  (value) => {
    selectedAccount.value = value
  }
)

watch(
  () => [props.internationalAccounts, props.paypalAccounts, props.revolutAccounts],
  () => {
    const allAccounts = [...props.internationalAccounts, ...props.paypalAccounts, ...props.revolutAccounts]

    if (!allAccounts.length) {
      selectedAccount.value = null

      return
    }

    const exists = allAccounts.some((item) => item.id === selectedAccount.value)

    if (!exists) {
      selectedAccount.value = null
    }
  },
  {
    deep: true,
  }
)
</script>

<template>
    <div ref="wrapper" class="relative flex flex-col gap-1 w-full text-[#1A1A1A] text-[13px] font-medium" :class="props.disabled
        ? 'cursor-not-allowed opacity-[0.5]'
        : ''
        ">
        <!-- Label -->

        <label v-if="label"
            class="w-fit bg-white px-1 transition-all z-30 duration-400 text-[#6C6C6C] pointer-events-none" :class="isOpen || selectedAccountData
                ? 'absolute ms-4 -mt-2 text-[10px] font-bold'
                : 'absolute mt-[13px] ms-5 text-[13px] font-medium'
                ">
            {{ label }}
        </label>

        <!-- Custom Select Wrapper -->

        <div class="relative w-full h-[48px]" :class="props.disabled
            ? 'cursor-not-allowed opacity-[0.5]'
            : ''
            ">
            <!-- Selected Value Display -->

            <div class="w-full h-full rounded-xl border px-[18px] pr-8 font-medium outline-none transition border-[#E0E0E0] focus:outline-none focus:ring-0 focus:border-[#000] bg-white flex items-center"
                :class="props.disabled
                    ? 'cursor-not-allowed'
                    : 'cursor-pointer'
                    " @click="toggleOpen">
                <span class="flex items-center gap-2 min-w-0 w-full overflow-hidden">
                    <template v-if="selectedAccountData">
                        <span class="shrink-0 truncate max-w-[45%]">
                            {{ selectedAccountData.bankName }}
                        </span>

                        <span v-if="
                            selectedAccountData.iban ||
                            selectedAccountData.accountNumber
                        " class="text-[#6C6C6C] font-sans truncate min-w-0">
                            -
                            {{
                                selectedAccountData.iban ||
                                selectedAccountData.accountNumber
                            }}
                        </span>
                    </template>

                    <template v-else>
                        {{ placeholder }}
                    </template>
                </span>
            </div>

            <!-- Chevron Icon -->

            <img src="/svg/chevron-down.svg" alt="chevron"
                class="absolute top-1/2 end-0.5 -translate-y-1/2 w-6 h-6 pointer-events-none transition-transform duration-200"
                :class="{
                    'rotate-180': isOpen
                }" />

            <!-- Custom Dropdown Options -->

            <div v-if="isOpen"
                class="absolute top-full pt-2 left-0 right-0 mt-1 bg-white border border-[#E0E0E0] rounded-xl shadow-lg z-[999] max-h-64 overflow-y-auto custom-dropdown">
                <!-- Loading -->

                <div v-if="loading" class="px-4 py-3 text-center">
                    در حال بارگذاری...
                </div>

                <!-- Transfer Methods + Accounts -->

                <div v-else-if="
                    groupedAccounts.length > 0
                ">
                    <div v-for="group in groupedAccounts" :key="group.transferMethod">
                        <!-- Transfer Method Checkbox -->


                        <label
                            class="px-4 py-3 cursor-pointer transition-all duration-200 flex items-center gap-3 hover:bg-[#f8f9fa]"
                            @click.stop>
                            <!-- Custom Checkbox -->
                            <span
                                class="w-5 h-5   shrink-0 rounded-[4px] border flex items-center justify-center transition-all duration-150"
                                :class="isTransferMethodSelected(group.transferMethod)
                                    ? 'bg-[#2626BF] border-[#2626BF]'
                                    : 'bg-white border-[#D1D1D1]'
                                    " @click="
                                        toggleTransferMethod(group.transferMethod)
                                        ">
                                <svg v-if="isTransferMethodSelected(group.transferMethod)" class="w-3 h-3 text-white"
                                    viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9.5 0.75L3.11625 7L0.75 4.58875" stroke="currentColor" stroke-width="1.5"
                                        stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </span>

                            <span @click="
                                toggleTransferMethod(group.transferMethod)
                                " class="text-[#6C6C6C] text-sm font-bold">
                                {{ group.transferMethodLabel }}
                            </span>
                        </label>


                        <!-- Accounts -->

                        <label v-for="account in group.accounts" :key="String(account.id)"
                            class="px-4 py-4 cursor-pointer transition-all duration-200 font-medium flex items-center gap-3 hover:bg-[#f8f9fa]">
                            <input type="radio" name="international-bank-account" :value="account.id" :checked="account.id ===
                                props.modelValue
                                " @change="
                                    updateValue(
                                        account.id
                                    )
                                    " class="w-4 h-4 accent-[#2626BF] shrink-0" />

                            <img v-if="account.bankIcon" :src="account.bankIcon" :alt="account.bankName"
                                class="w-6 h-6 object-contain shrink-0" />

                            <img v-else src="/svg/bankIcon.svg" alt="bank" class="w-6 h-6 shrink-0" />

                            <div class="flex flex-col gap-1 font-fa min-w-0">
                                <span class="text-[#1A1A1A] text-sm truncate">
                                    {{
                                        account.bankName
                                    }}
                                </span>

                                <!-- <span v-if="
                                    account.ownerFullName
                                " class="text-[#6C6C6C] text-xs truncate">
                                    {{
                                        account.ownerFullName
                                    }}
                                </span> -->

                                <span v-if="
                                    account.iban ||
                                    account.accountNumber
                                " class="text-[#6C6C6C] text-xs font-sans truncate">
                                    {{
                                        (
                                            account.iban ||
                                            account.accountNumber
                                        )?.replace(
                                            /(.{4})/g,
                                            '$1 '
                                        )
                                    }}
                                </span>
                            </div>
                        </label>
                    </div>
                </div>

                <!-- Empty -->

                <div v-else class="px-4 py-3 text-center">
                    حسابی یافت نشد
                </div>

                <!-- حساب جدید -->

                <div @click="openCreateAccountModal"
                    class="px-4 py-3 cursor-pointer transition-all duration-200 text-[#0E59FA] flex items-center gap-2 hover:bg-[#f8f9fa]">
                    <span class="text-xl font-thin pt-1">
                        +
                    </span>

                    حساب جدید
                </div>
            </div>
        </div>

        <!-- Create Bank Account Drawer -->

        <CreateBankAccountDrawer v-model="isCreateAccountModalOpen" :countries="countries" />

        <!-- Error -->

        <div v-if="error" class="flex items-center gap-1 text-[#ed145b] mt-1">
            <img src="/svg/error-icon.svg" width="16" alt="error-icon" />

            {{ error }}
        </div>
    </div>
</template>

<style scoped>
.custom-dropdown::-webkit-scrollbar {
    width: 6px;
}

.custom-dropdown::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.custom-dropdown::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

.custom-dropdown::-webkit-scrollbar-thumb:hover {
    background: #a1a1a1;
}
</style>